import axios from 'axios';
import Cookies from 'js-cookie';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = Cookies.get('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      Cookies.remove('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  getMe: () => api.get('/auth/me'),
};

// Contacts API
export const contactsAPI = {
  getAll: (params) => api.get('/contacts', { params }),
  getById: (id) => api.get(`/contacts/${id}`),
  create: (data) => api.post('/contacts', data),
  update: (id, data) => api.put(`/contacts/${id}`, data),
  delete: (id) => api.delete(`/contacts/${id}`),
  bulkDelete: (contactIds) => api.delete('/contacts/bulk', { data: { contact_ids: contactIds } }),
  export: (contactIds) => {
    const exportApi = axios.create({
      baseURL: API_BASE_URL,
      responseType: 'blob',
      withCredentials: true,
    });
    
    const token = Cookies.get('token');
    if (token) {
      exportApi.defaults.headers.Authorization = `Bearer ${token}`;
    }
    
    return exportApi.post('/contacts/export', { contact_ids: contactIds });
  },
  import: (formData) => {
    const importApi = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      withCredentials: true,
    });
    
    const token = Cookies.get('token');
    if (token) {
      importApi.defaults.headers.Authorization = `Bearer ${token}`;
    }
    
    return importApi.post('/contacts/import', formData);
  },
  downloadTemplate: () => {
    const token = Cookies.get('token');
    return api.get('/contacts/download-template', {
      headers: {
        'Authorization': `Bearer ${token}`
      },
      responseType: 'blob'
    });
  }
};

// Families API
export const familiesAPI = {
  getAll: () => api.get('/families'),
  create: (data) => api.post('/families', data),
  update: (id, data) => api.put(`/families/${id}`, data),
  delete: (id) => api.delete(`/families/${id}`),
  getSubFamilies: (id) => api.get(`/families/${id}/subfamilies`),
  createSubFamily: (id, data) => api.post(`/families/${id}/subfamilies`, data),
  updateSubFamily: (subId, data) => api.put(`/families/subfamilies/${subId}`, data),
  deleteSubFamily: (subId) => api.delete(`/families/subfamilies/${subId}`),
};

// Users API
export const usersAPI = {
  getAll: () => api.get('/users'),
  updateProfile: (data) => api.put('/users/profile', data),
  changePassword: (data) => api.put('/users/password', data),
  getShareable: () => api.get('/users/shareable'),
  getSharedContacts: () => api.get('/users/shared-contacts'),
  updateStatus: (id, data) => api.put(`/users/${id}/status`, data),
};

// Emails API
export const emailsAPI = {
  send: (data) => api.post('/emails/send', data),
  getLogs: () => api.get('/emails/logs'),
};

// Events API
export const eventsAPI = {
  getAll: () => api.get('/events'),
  create: (data) => api.post('/events', data),
  update: (id, data) => api.put(`/events/${id}`, data),
  delete: (id) => api.delete(`/events/${id}`),
  toggle: (id) => api.put(`/events/${id}/toggle`),
};

// Shares API
export const sharesAPI = {
  getAll: () => api.get('/shares'),
  create: (data) => api.post('/shares', data),
  bulkCreate: (data) => api.post('/shares/bulk', data),
  delete: (id) => api.delete(`/shares/${id}`),
  getContactShares: (contactId) => api.get(`/shares/contact/${contactId}`),
};

export default api;
